import React, { createContext, useContext, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { ApiClient } from './client';

interface ApiProviderProps {
  children: React.ReactNode;
  baseURL?: string;
}

interface ApiContextValue {
  client: ApiClient;
}

const ApiContext = createContext<ApiContextValue | null>(null);

export function ApiProvider({ children, baseURL = '/api' }: ApiProviderProps) {
  const { instance, accounts } = useMsal();

  const client = useMemo(() => {
    const getToken = async () => {
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ['User.Read', 'openid', 'profile', 'email'],
          account: accounts[0],
        });
        return response.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.loginRedirect();
        }
        throw error;
      }
    };

    return new ApiClient({
      baseURL,
      getToken,
      onAuthError: () => instance.loginRedirect(),
    });
  }, [instance, accounts, baseURL]);

  return (
    <ApiContext.Provider value={{ client }}>{children}</ApiContext.Provider>
  );
}

export function useApi() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context.client;
}
